/* ==========================================================================
   Collapsable class
   ========================================================================== */

interface CollapsableManagerOptions {
  selector?: string
  autoInit?: boolean
  collapsableElements?: NodeListOf<HTMLElement> | null
}

export default class CollapsableManager {
  selector: string = ''
  collapsableList: Collapsable[] = []

  private _htmlElements: NodeListOf<HTMLElement> | null = null

  constructor ({
    selector = '[data-ui-el="nfb-collapsable-item-trigger"]',
    autoInit = true,
    collapsableElements = null
  }: CollapsableManagerOptions = {}) {
    this.selector = selector
    if (autoInit) {
      this.init(collapsableElements)
    }
  }

  init (collapsableElements: NodeListOf<HTMLElement> | null): void {
    if (collapsableElements) {
      this._htmlElements = collapsableElements
    } else {
      this._htmlElements = document.querySelectorAll(this.selector)
    }
    if (this._htmlElements.length > 0) {
      this._htmlElements.forEach(el => {
        this.collapsableList.push(new Collapsable(el))
      })
    }
  }

  destroy (): void {
    this.collapsableList.forEach(collapsable => { collapsable.destroy() })
  }
}

class Collapsable {
  trigger: HTMLElement | null = null

  private _isExpanded: boolean = false

  constructor (trigger: HTMLElement) {
    this.trigger = trigger

    if (!(this.trigger instanceof HTMLElement)) {
      throw new Error('No trigger found for collapsable')
    }

    if (this.trigger.getAttribute('aria-expanded') === 'true') {
      this.isExpanded = true
    }

    this.trigger.addEventListener('click', this.toggle)

    if (this.trigger.nodeName !== 'BUTTON') {
      // add listeners for space & enter
      this.trigger.setAttribute('role', 'button')
      this.trigger.setAttribute('tabindex', '0')
      this.trigger.addEventListener('keydown', this.keyDownToggle)
    }
  }

  destroy (): void {
    this.trigger?.removeEventListener('click', this.toggle)
    this.trigger?.removeEventListener('keydown', this.keyDownToggle)
  }

  /* Setters & Getters */

  get isExpanded (): boolean { return this._isExpanded }

  set isExpanded (value: boolean) {
    this._isExpanded = value
    this.trigger?.setAttribute('aria-expanded', value.toString())
  }

  /* Methods */

  toggle = (): void => {
    this.isExpanded = !this.isExpanded
  }

  keyDownToggle = (e: KeyboardEvent): void => {
    const key: string = e.key
    if (
      (key === 'Enter') ||
      (['Spacebar', ' '].includes(key))
    ) {
      // Default behavior is prevented to prevent the page to scroll when "space" is pressed
      e.preventDefault()
      this.toggle()
    }
  }
}
